import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Salon from "../../assets/beauty/salon-app.png";
import BarberWeb from "../../assets/beauty/mac-essenza.png";
import BeautyBackground from "../../assets/beauty/beauty-bg3.jpg";
import BeautyBackground2 from "../../assets/beauty/beauty-bg2.jpg";
import { useTranslation } from "react-i18next";

import {
	Check,
	Palette,
	Typography,
	LayoutDashboard,
	Photo,
} from "tabler-icons-react";

const primaryColor = "#D9B8CE"; // Rosa empolvado
const accentColor = "#43BD98"; // Verde Applaby

const section = {
	padding: "6rem 2rem",
	fontFamily: "'Poppins', sans-serif",
};

const container = {
	maxWidth: "1200px",
	margin: "0 auto",
};

const image = {
	width: "100%",
	maxWidth: "500px",
	borderRadius: "1.2rem",
	display: "block",
	margin: "0 auto",
};

const button = {
	display: "inline-block",
	padding: "1rem 2rem",
	borderRadius: "10px",
	fontWeight: 600,
	backgroundColor: accentColor,
	color: "#fff",
	textDecoration: "none",
	transition: "all 0.3s ease",
	marginTop: "8px",
	boxShadow: "0 4px 12px rgba(67,189,152,0.3)",
};

const badge = {
	display: "inline-block",
	backgroundColor: "#fdf2f7",
	color: primaryColor,
	fontSize: "0.85rem",
	fontWeight: 600,
	padding: "0.4rem 0.8rem",
	borderRadius: "20px",
	marginBottom: "1.2rem",
};

export default function WebDemo() {
	const [offsetY, setOffsetY] = useState(0);
	const { t } = useTranslation();

	useEffect(() => {
		const handleScroll = () => setOffsetY(window.scrollY);
		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	return (
		<div style={{ width: "100%", overflowX: "hidden" }}>
			{/* HERO */}
			<section
				className="section hero"
				style={{
					...section,
					position: "relative",
					textAlign: "center",
					color: "#333",
					backgroundImage: `url(${BeautyBackground})`,
					backgroundSize: "cover",
					backgroundRepeat: "no-repeat",
					backgroundPosition: `center ${offsetY * 0.4}px`,
				}}
			>
				<div
					style={{
						position: "absolute",
						inset: 0,
						background: "rgba(255,255,255,0.8)",
						backdropFilter: "blur(2px)",
						zIndex: 0,
					}}
				/>

				<div
					style={{
						...container,
						position: "relative",
						zIndex: 1,
						marginTop: 80,
					}}
				>
					<motion.h1
						className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight mb-10"
						initial={{ opacity: 0, y: -20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.7, ease: "easeOut" }}
						style={{
							lineHeight: 1.2,
							maxWidth: 800,
							margin: "0 auto", // 👈 centra horizontalmente
							textAlign: "center",
							marginBottom: 20,
						}}
					>
						{t("beautyHero.demoWeb.hero.title")}
					</motion.h1>

					<motion.p
						initial={{ opacity: 0, y: 30 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8, delay: 0.2 }}
						className="text-white-600 text-base md:text-lg max-w-lg mx-auto leading-relaxed mb-10 text-center"
					>
						{t("beautyHero.demoWeb.hero.subtitle")}
					</motion.p>

					<motion.div
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8, delay: 0.1 }}
						viewport={{ once: true }}
					>
						<a
							href="https://applaby.com/app-demo/"
							target="_blank"
							rel="noreferrer"
							style={button}
						>
							{t("beautyHero.demoWeb.hero.button")}
						</a>
					</motion.div>
				</div>

				{/* Ola inferior */}
				<div
					className="absolute bottom-0 left-0 w-full overflow-hidden leading-none"
					style={{ marginBottom: -1 }}
				>
					<svg
						className="relative block w-full h-[90px] sm:h-[100px]"
						viewBox="0 0 1440 80"
						xmlns="http://www.w3.org/2000/svg"
						preserveAspectRatio="none"
					>
						<path
							d="M0,80 C480,20 960,140 1440,20 L1440,80 L0,80 Z"
							fill="#ffffff"
						/>
					</svg>
				</div>
			</section>
			{/* SECCIÓN 1: APP MÓVIL */}
			<section
				className="section"
				style={{
					...section,
					backgroundColor: "#ffffff",
					color: "#333",
					padding: "8rem 8rem",
				}}
			>
				<div
					className="two-col"
					style={{
						...container,
						display: "grid",
						gridTemplateColumns: "1fr 1fr",
						alignItems: "center",
						gap: "3rem",
					}}
				>
					<motion.div
						initial={{ opacity: 0, x: -60 }}
						whileInView={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.8, ease: "easeOut" }}
						viewport={{ once: true }}
					>
						<span
							style={{
								display: "inline-block",
								fontSize: "0.9rem",
								fontWeight: 600,
								textTransform: "uppercase",
								padding: "0.4rem 1rem",
								borderRadius: "50px",
								backgroundColor: "#f8e9f1",
								color: primaryColor,
								border: "1px solid #f2d6e3",
								marginBottom: "1.5rem",
							}}
						>
							{t("beautyHero.demoWeb.app.badge")}
						</span>

						<h2
							style={{
								fontSize: "clamp(1.8rem, 5vw, 2.6rem)",
								fontWeight: 800,
								marginBottom: 40,
							}}
						>
							{t("beautyHero.demoWeb.app.title")}
						</h2>

						<p
							style={{
								fontSize: "clamp(1rem, 1.8vw, 18px)",
								lineHeight: 1.7,
								maxWidth: "700px",
								marginBottom: 40,
							}}
						>
							{t("beautyHero.demoWeb.app.description")}
						</p>

						<ul
							style={{
								listStyle: "none",
								padding: 0,
								margin: 0,
								textAlign: "left", // 👈 fuerza alineación a la izquierda
								maxWidth: "500px", // 👈 evita que se estire demasiado en móvil
								marginInline: "auto",
							}}
						>
							{t("beautyHero.demoWeb.app.features", {
								returnObjects: true,
							}).map((item, i) => (
								<motion.li
									key={i}
									initial={{ opacity: 0, x: -20 }}
									whileInView={{ opacity: 1, x: 0 }}
									transition={{ duration: 0.5, delay: 0.4 + i * 0.1 }}
									viewport={{ once: true }}
									style={{
										display: "flex",
										alignItems: "center",
										marginBottom: "1rem",
										fontSize: "1rem",
										color: "#444",
									}}
								>
									<span
										style={{
											display: "inline-flex",
											justifyContent: "center",
											alignItems: "center",
											width: "32px",
											height: "32px",
											borderRadius: "50%",
											backgroundColor: primaryColor,
											color: "#fff",
											marginRight: "0.8rem",
										}}
									>
										<Check size={20} strokeWidth={2.5} />
									</span>
									{item}
								</motion.li>
							))}
						</ul>
					</motion.div>

					<motion.div
						initial={{ opacity: 0, x: 50 }}
						whileInView={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.8, delay: 0.2 }}
						viewport={{ once: true }}
						style={{ textAlign: "center", alignSelf: "center", marginTop: 80 }}
					>
						<img
							src={Salon}
							alt={t("beautyHero.demoWeb.app.imageAlt")}
							style={image}
						/>
					</motion.div>
				</div>
			</section>

			{/* SECCIÓN 2: WEB DE RESERVAS */}
			<section
				className="section"
				style={{
					...section,
					backgroundColor: "#fff9fc",
					color: "#111",
					padding: "10rem 8rem",
				}}
			>
				<div
					className="two-col"
					style={{
						...container,
						display: "grid",
						gridTemplateColumns: "1fr 1fr",
						alignItems: "center",
						gap: "4rem",
					}}
				>
					<motion.div
						initial={{ opacity: 0, x: -50 }}
						whileInView={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.8 }}
						viewport={{ once: true }}
					>
						<span style={badge}>{t("beautyHero.demoWeb.web.badge")}</span>

						<h2
							style={{
								fontSize: "clamp(1.8rem, 5vw, 2.6rem)",
								fontWeight: 800,
								marginBottom: "1rem",
							}}
						>
							{t("beautyHero.demoWeb.web.title")}
						</h2>

						<p
							style={{
								fontSize: "clamp(1rem, 1.8vw, 18px)",
								lineHeight: 1.7,
								maxWidth: "700px",
								marginBottom: 40,
							}}
						>
							{t("beautyHero.demoWeb.web.description")}
						</p>

						<a
							href="https://book.applaby.com/essenza"
							target="_blank"
							rel="noreferrer"
							style={button}
						>
							{t("beautyHero.demoWeb.web.button")}
						</a>
					</motion.div>

					<motion.div
						initial={{ opacity: 0, x: 50 }}
						whileInView={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.8, delay: 0.2 }}
						viewport={{ once: true }}
						style={{ textAlign: "center" }}
					>
						<img
							src={BarberWeb}
							alt={t("beautyHero.demoWeb.web.imageAlt")}
							style={image}
						/>
					</motion.div>
				</div>
			</section>

			{/* SECCIÓN 3: Gestión de citas */}
			<section
				className="section"
				style={{
					...section,
					backgroundColor: "#fff9fc",
					textAlign: "center",
					position: "relative",
				}}
			>
				<div style={{ ...container }}>
					<motion.div
						initial={{ opacity: 0, y: 40 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8 }}
						viewport={{ once: true }}
						style={{ marginBottom: "1.5rem" }}
					>
						<span style={badge}>{t("beautyHero.demoWeb.booking.badge")}</span>

						<h2
							style={{
								fontSize: "clamp(1.8rem, 5vw, 2.6rem)",
								fontWeight: 800,
								margin: 0,
								marginBottom: 40,
								color: "#222",
							}}
						>
							{t("beautyHero.demoWeb.booking.title")}
						</h2>
					</motion.div>

					<motion.div
						initial={{ opacity: 0, y: 40 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8, delay: 0.2 }}
						viewport={{ once: true }}
						style={{
							padding: "0rem 2rem",
							maxWidth: "650px",
							margin: "0 auto",
						}}
					>
						<p
							style={{
								fontSize: "clamp(1rem, 1.8vw, 18px)",
								lineHeight: 1.7,
								maxWidth: "700px",
								marginBottom: 40,
								color: "#444",
							}}
						>
							{t("beautyHero.demoWeb.booking.description")}
						</p>

						{/* 
			<a
				href="https://applaby.com/es/demo-belleza/"
				style={{
					...button,
					backgroundColor: "#fff",
					color: accentColor,
					border: `2px solid ${accentColor}`,
					boxShadow: "none",
				}}
				target="_blank"
				rel="noreferrer"
			>
				{t("beautyHero.demoWeb.booking.button")}
			</a>
			*/}
					</motion.div>
				</div>
			</section>

			{/* SECCIÓN 4: Personalización */}
			<section
				className="section"
				style={{
					...section,
					backgroundColor: "#ffffff",
					color: "#333",
					textAlign: "center",
				}}
			>
				<div style={{ ...container, textAlign: "center", marginBottom: 20 }}>
					{/* Badge */}
					<motion.span
						initial={{ opacity: 0 }}
						whileInView={{ opacity: 1 }}
						transition={{ duration: 0.6 }}
						viewport={{ once: true }}
						style={{
							display: "inline-block",
							fontSize: "0.9rem",
							fontWeight: "600",
							textTransform: "uppercase",
							letterSpacing: "0.5px",
							padding: "0.4rem 1rem",
							borderRadius: "50px",
							backgroundColor: "#fdf2f7",
							color: primaryColor,
							border: "1px solid #f2d6e3",
							marginBottom: "1.5rem",
						}}
					>
						{t("beautyHero.demoWeb.customization.badge")}
					</motion.span>

					{/* Título */}
					<h2
						style={{
							fontSize: "clamp(1.8rem, 5vw, 2.6rem)",
							fontWeight: 800,
							margin: 0,
							marginBottom: 40,
							color: "#222",
						}}
					>
						{t("beautyHero.demoWeb.customization.title")}
					</h2>

					{/* Subtítulo */}
					<motion.p
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8, delay: 0.2 }}
						viewport={{ once: true }}
						style={{
							fontSize: "clamp(1rem, 1.8vw, 18px)",
							color: "#555",
							lineHeight: 1.6,
							margin: "0 auto 3rem",
							maxWidth: "600px",
						}}
					>
						{t("beautyHero.demoWeb.customization.description")}
					</motion.p>

					{/* Tarjetas */}
					<div
						style={{
							display: "grid",
							gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
							gap: "2rem",
							marginTop: "2rem",
						}}
					>
						{t("beautyHero.demoWeb.customization.features", {
							returnObjects: true,
						}).map((f, i) => (
							<motion.div
								key={i}
								initial={{ opacity: 0, y: 40 }}
								whileInView={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.6, delay: i * 0.2, ease: "easeOut" }}
								viewport={{ once: true }}
								style={{
									background: "#fff",
									borderRadius: "1.2rem",
									border: "1px solid #eee",
									padding: "2rem",
									boxShadow: "0 6px 20px rgba(0,0,0,0.05)",
									transition: "all 0.3s ease",
									cursor: "default",
								}}
								onMouseEnter={(e) => {
									e.currentTarget.style.transform = "translateY(-8px)";
									e.currentTarget.style.boxShadow =
										"0 12px 30px rgba(217,184,206,0.25)";
									e.currentTarget.style.border = `1px solid ${primaryColor}`;
								}}
								onMouseLeave={(e) => {
									e.currentTarget.style.transform = "translateY(0)";
									e.currentTarget.style.boxShadow =
										"0 6px 20px rgba(0,0,0,0.05)";
									e.currentTarget.style.border = "1px solid #eee";
								}}
							>
								{/* Icono */}
								<div
									style={{
										width: 56,
										height: 56,
										margin: "0 auto 1.2rem",
										borderRadius: "50%",
										background: "#f8f8f8",
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
									}}
								>
									{f.icon === "palette" && (
										<Palette size={28} color={accentColor} />
									)}
									{f.icon === "typography" && (
										<Typography size={28} color={accentColor} />
									)}
									{f.icon === "dashboard" && (
										<LayoutDashboard size={28} color={accentColor} />
									)}
									{f.icon === "photo" && (
										<Photo size={28} color={accentColor} />
									)}
								</div>

								{/* Título */}
								<h3
									style={{
										fontSize: "1.25rem",
										fontWeight: 600,
										color: "#222",
										marginBottom: "0.6rem",
									}}
								>
									{f.title}
								</h3>

								{/* Descripción */}
								<p
									style={{
										color: "#555",
										fontSize: "1rem",
										lineHeight: 1.6,
										maxWidth: 280,
										margin: "0 auto",
									}}
								>
									{f.desc}
								</p>
							</motion.div>
						))}
					</div>
				</div>
			</section>

			{/* CTA FINAL */}
			<section
				className="section cta-final"
				style={{
					position: "relative",
					padding: "4rem 2rem",
					color: "#333",
					textAlign: "center",
					fontFamily: "'Poppins', sans-serif",
					backgroundImage: `url(${BeautyBackground2})`,
					backgroundSize: "cover",
					backgroundPosition: "center",
				}}
			>
				<div
					style={{
						position: "absolute",
						inset: 0,
						background: "rgba(255,255,255,0.85)",
						zIndex: 0,
					}}
				/>

				<div
					style={{
						position: "relative",
						zIndex: 1,
						maxWidth: "800px",
						margin: "0 auto",
					}}
				>
					<motion.h2
						style={{
							fontSize: "34px",
							fontWeight: 800,
							marginBottom: "1.5rem",
							lineHeight: 1.2,
							color: "#222",
						}}
						initial={{ opacity: 0, y: 30 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8 }}
						viewport={{ once: true }}
					>
						{t("beautyHero.demoWeb.cta.title")}
					</motion.h2>

					<motion.p
						style={{
							fontSize: "18px",
							lineHeight: 1.7,
							color: "#444",
							marginBottom: "2.5rem",
						}}
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8, delay: 0.2 }}
						viewport={{ once: true }}
					>
						{t("beautyHero.demoWeb.cta.subtitle")}
					</motion.p>

					<a
						href="https://applaby.com/app-demo/"
						target="_blank"
						rel="noreferrer"
						style={button}
					>
						{t("beautyHero.demoWeb.cta.button")}
					</a>
				</div>
			</section>

			{/* RESPONSIVE */}

			<style>{`
  @media (max-width: 768px) {
   .section { padding: 4rem 1.5rem !important; } .two-col { grid-template-columns: 1fr !important; text-align: center; gap: 0 !important; } .two-col div { margin-top: 40px !important; } .two-col img { margin: 0 auto !important; margin-top: 0px !important; } }
  }
`}</style>
		</div>
	);
}
